import React, { useState } from 'react';
import Field from '../Field';
import { DateInput } from '../RegionInputs';
import { REPORTS_WORDING as W, reportRangeFromLabel } from '../../domain/reports';
import s from './reports.module.css';

// One parameter of a report, rendered from its catalogue entry.
//
// The form is generated from domain/reports rather than hand-built for each
// report type, so adding one is a registry entry; this is the switch that
// keeps that true.

function RadioParam({ param, value, onChange }) {
  return (
    <div className={s.param}>
      <div className={s.paramLabel}>{param.label}</div>
      {param.options.map((option) => {
        // An option the app cannot honour is shown and disabled with the reason,
        // rather than removed — removing it hides that the gap exists, and
        // leaving it selectable hands over a different file type than was asked
        // for.
        const why = param.unavailable?.[option];
        return (
          <label key={option} className={why ? s.choiceDisabled : s.choice}>
            <input
              type="radio" name={param.id}
              checked={value === option}
              disabled={Boolean(why)}
              onChange={() => onChange(option)}
            />
            <span>{option}</span>
            {why ? <span className={s.unavailable}>{why}</span> : null}
          </label>
        );
      })}
    </div>
  );
}

// A multi-select: Revenue & Expenses' Categories and Payees pickers (#601),
// the lesson Categories of Attendance and Lessons Provided, and a checklist
// (#795). `param.options` is a checklist's own, or else the list this
// report's own data offers — computed in ReportsPage, the same way
// `reportFormatParam`'s availability is — and the value is the chosen subset.
// For a filter, empty means "all" (the hint shown below the label).
function MultiSelectParam({ param, value, onChange }) {
  const chosen = Array.isArray(value) ? value : [];
  const toggle = (option) => onChange(chosen.includes(option) ? chosen.filter((v) => v !== option) : [...chosen, option]);
  return (
    <div className={s.param}>
      <div className={s.paramLabel}>{param.label}</div>
      {param.hint ? <div className={s.paramHint}>{param.hint}</div> : null}
      {(param.options || []).map((option) => (
        <label key={option} className={s.choice}>
          <input type="checkbox" checked={chosen.includes(option)} onChange={() => toggle(option)} />
          <span>{option}</span>
        </label>
      ))}
    </div>
  );
}

// MMS's Students control (#795): the chosen students as chips, "All
// Students" while there are none, a type-ahead to add one, Clear Selection,
// "Include inactive students in list", and a Quick Select that adds a group.
// The value is the chosen ids. `param.choices(includeInactive)` is the
// core's list (`reportStudentChoices`) and `param.quickSelects` its Quick
// Select, both handed in by ReportsPage.
function StudentsParam({ param, value, onChange }) {
  const [query, setQuery] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const chosen = Array.isArray(value) ? value : [];
  const listed = param.choices ? param.choices(includeInactive) : [];
  const named = new Map((param.choices ? param.choices(true) : []).map((c) => [c.id, c.name]));
  const add = (ids) => onChange([...chosen, ...ids.filter((id) => !chosen.includes(id))]);
  const typed = query.trim().toLocaleLowerCase();
  const matches = typed
    ? listed.filter((c) => !chosen.includes(c.id) && c.name.toLocaleLowerCase().includes(typed))
    : [];
  return (
    <div className={s.param}>
      <div className={s.paramLabel}>{param.label}</div>
      <div className={s.chips}>
        {chosen.length === 0 ? <span className={s.paramHint}>{param.hint}</span> : null}
        {chosen.map((id) => (
          <span key={id} className={s.chip}>
            {named.get(id) || id}
            <button
              type="button" className={s.chipRemove} aria-label={`Remove ${named.get(id) || id}`}
              onClick={() => onChange(chosen.filter((x) => x !== id))}
            >
              ×
            </button>
          </span>
        ))}
      </div>
      <input
        type="text" className={s.input} value={query} placeholder={W.studentsSearch}
        aria-label={W.studentsSearch}
        onChange={(e) => setQuery(e.target.value)}
      />
      {matches.length ? (
        <div className={s.matches} role="listbox">
          {matches.map((c) => (
            <button
              key={c.id} type="button" role="option" aria-selected={false} className={s.match}
              onClick={() => { add([c.id]); setQuery(''); }}
            >
              {c.name}
            </button>
          ))}
        </div>
      ) : null}
      <div className={s.studentTools}>
        <button type="button" className={s.linkButton} onClick={() => onChange([])} disabled={chosen.length === 0}>
          {W.clearSelection}
        </button>
        {param.quickSelects?.length ? (
          <select
            className={s.quickSelect} value="" aria-label={W.quickSelect}
            onChange={(e) => {
              const group = param.quickSelects[Number(e.target.value)];
              if (group) add(group.studentIds);
            }}
          >
            <option value="">{W.quickSelect}</option>
            {param.quickSelects.map((q, i) => <option key={q.label} value={i}>{q.label}</option>)}
          </select>
        ) : null}
      </div>
      <label className={s.checkRow}>
        <input type="checkbox" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} />
        <span>{W.includeInactive}</span>
      </label>
    </div>
  );
}

function DateRangeParam({ param, value, onChange }) {
  // Stored as "start..end" in one field, so a report's parameters stay a flat
  // map of strings and survive being written to a document unchanged.
  const [start = '', end = ''] = String(value || '').split('..');
  return (
    <div className={s.paramPair}>
      <Field label={reportRangeFromLabel(param.label)}>
        <DateInput
          className={s.input} value={start}
          onChange={(e) => onChange(`${e.target.value}..${end}`)}
        />
      </Field>
      <Field label={W.rangeTo}>
        <DateInput
          className={s.input} value={end}
          onChange={(e) => onChange(`${start}..${e.target.value}`)}
        />
      </Field>
    </div>
  );
}

export default function ParamField({ param, value, onChange }) {
  if (param.type === 'radio') return <RadioParam param={param} value={value} onChange={onChange} />;
  if (param.type === 'dateRange') return <DateRangeParam param={param} value={value} onChange={onChange} />;
  // A checklist's options are its own; the other three lists' are the
  // studio's, handed in by ReportsPage.
  if (['categories', 'payees', 'eventCategories', 'checklist'].includes(param.type)) {
    return <MultiSelectParam param={param} value={value} onChange={onChange} />;
  }
  if (param.type === 'students') return <StudentsParam param={param} value={value} onChange={onChange} />;

  if (param.type === 'checkbox') {
    const row = (
      <label className={s.checkRow}>
        <input
          type="checkbox" checked={Boolean(value)}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span>{param.label}</span>
      </label>
    );
    // Include Attachments' hint (#623): the receipts go into the PDF only.
    if (!param.hint) return row;
    return (
      <div>
        {row}
        <div className={s.paramHint}>{param.hint}</div>
      </div>
    );
  }

  if (param.type === 'textarea') {
    return (
      <div className={s.param}>
        <Field label={param.label} hint={param.hint}>
          <textarea
            rows={3} className={s.input} value={value || ''}
            onChange={(e) => onChange(e.target.value)}
          />
        </Field>
      </div>
    );
  }

  const isDate = param.type === 'date';
  return (
    <div className={isDate ? s.paramNarrow : s.param}>
      <Field label={param.label} hint={param.hint}>
        {isDate ? (
          <DateInput
            className={s.input}
            value={value || ''}
            placeholder={param.hint}
            onChange={(e) => onChange(e.target.value)}
          />
        ) : (
          <input
            type="text"
            className={s.input}
            value={value || ''}
            placeholder={param.hint}
            onChange={(e) => onChange(e.target.value)}
          />
        )}
      </Field>
    </div>
  );
}
